import React, { RefObject, useEffect } from 'react';
import LazyLoad from 'react-lazyload';

type CardTileProps = {
  imgRef?: RefObject<HTMLImageElement>;
  onReady?: () => void;
  containerStyles?: Record<string, string>;
  hasNoBackground?: boolean;
} & React.ClassAttributes<HTMLImageElement> &
  React.ImgHTMLAttributes<HTMLImageElement>;

export const CardImage: React.FC<CardTileProps> = ({
  imgRef,
  src,
  alt = '',
  onReady,
  containerStyles,
  ...props
}) => {
  useEffect(() => {
    if (onReady && imgRef?.current) {
      const element = imgRef.current;

      element.addEventListener('load', onReady);

      return () => {
        element?.removeEventListener('load', onReady);
      };
    }

    return () => undefined;
  }, [onReady, imgRef]);

  if (!src) return null;

  return (
    <picture style={containerStyles}>
      <source srcSet={`${src}?w=${400}`} media="(max-width: 768px)" />
      <source srcSet={`${src}?w=${232}`} media="(max-width: 991px)" />
      <source srcSet={`${src}?w=${448}`} media="(min-width: 992px)" />
      <img
        ref={imgRef}
        style={{ borderRadius: '16px' }}
        src={`${src}?w=350`}
        alt={alt}
        {...props}
      />
    </picture>
  );
};

type LazyCardTileProps = CardTileProps & { offset?: number; bypassLazy?: boolean };

export const LazyCardImage: React.FC<LazyCardTileProps> = ({
  offset = 100,
  bypassLazy,
  ...props
}) => {
  if (bypassLazy) {
    return <CardImage {...props} />;
  }

  return (
    <LazyLoad offset={offset}>
      <CardImage {...props} />
    </LazyLoad>
  );
};
